import React, { useEffect, useId, useRef, useState } from 'react';
import apiService from '../../services/ApiService';

export interface JudgeUserOption {
  id: number;
  display_name: string;
  username: string;
  role: string;
}

interface JudgeUserComboboxProps {
  eventId: number;
  selected: JudgeUserOption | null;
  onSelect: (user: JudgeUserOption | null) => void;
  disabled?: boolean;
  inputClassName?: string;
}

const ROLE_LABEL: Record<string, string> = {
  user: 'Member',
  judge: 'Judge',
  organizer: 'Organizer',
  super_admin: 'Admin',
};

/**
 * Debounced user search for assigning event judges.
 * Keyboard-accessible combobox; does not expose email.
 */
const JudgeUserCombobox: React.FC<JudgeUserComboboxProps> = ({
  eventId,
  selected,
  onSelect,
  disabled = false,
  inputClassName = '',
}) => {
  const listboxId = useId();
  const inputId = useId();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<JudgeUserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setHighlight(-1);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const runSearch = (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      setSearched(false);
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setSearched(true);
    apiService
      .searchJudgeUsers(trimmed, { eventId, limit: 10 })
      .then((list) => {
        if (reqId !== reqIdRef.current) return;
        setResults(list);
        setLoading(false);
        setHighlight(list.length > 0 ? 0 : -1);
      })
      .catch(() => {
        if (reqId !== reqIdRef.current) return;
        setResults([]);
        setLoading(false);
      });
  };

  const scheduleSearch = (q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 250);
  };

  const handleSelect = (user: JudgeUserOption) => {
    onSelect(user);
    setQuery('');
    setResults([]);
    setOpen(false);
    setHighlight(-1);
    setSearched(false);
  };

  const handleClear = () => {
    onSelect(null);
    setQuery('');
    setResults([]);
    setSearched(false);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? results.length - 1 : h - 1));
    } else if (e.key === 'Enter' && highlight >= 0 && results[highlight]) {
      e.preventDefault();
      handleSelect(results[highlight]);
    }
  };

  if (selected) {
    return (
      <div className="rounded-xl border border-emerald-800/40 bg-emerald-950/20 px-4 py-3">
        <p className="text-[10px] uppercase tracking-wider text-emerald-500/80 mb-1">
          Selected judge
        </p>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-medium text-white truncate">{selected.display_name}</p>
            <p className="text-sm text-gray-400">@{selected.username}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {ROLE_LABEL[selected.role] || selected.role}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="text-sm text-gray-400 hover:text-white min-h-[40px] px-2 shrink-0"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative">
      <label htmlFor={inputId} className="block text-xs text-gray-400 mb-1">
        Search by name or username
      </label>
      <input
        ref={inputRef}
        id={inputId}
        type="search"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          highlight >= 0 && results[highlight]
            ? `${listboxId}-opt-${results[highlight].id}`
            : undefined
        }
        value={query}
        disabled={disabled}
        placeholder="Start typing a name…"
        autoComplete="off"
        className={inputClassName}
        onChange={(e) => {
          const next = e.target.value;
          setQuery(next);
          onSelect(null);
          setOpen(true);
          scheduleSearch(next);
        }}
        onFocus={() => {
          setOpen(true);
          if (query.trim().length >= 2) scheduleSearch(query);
        }}
        onKeyDown={onKeyDown}
      />
      {open && query.trim().length >= 2 && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-40 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-white/[0.08] bg-[#0a0a0a] shadow-xl"
        >
          {loading && (
            <p className="px-4 py-3 text-sm text-gray-500">Searching…</p>
          )}
          {!loading && searched && results.length === 0 && (
            <p className="px-4 py-3 text-sm text-gray-500">No matching users</p>
          )}
          {!loading &&
            results.map((u, i) => (
              <button
                key={u.id}
                id={`${listboxId}-opt-${u.id}`}
                type="button"
                role="option"
                aria-selected={i === highlight}
                className={`w-full text-left px-4 py-3 min-h-[48px] border-b border-white/[0.06] last:border-0 ${
                  i === highlight ? 'bg-[#141414]' : 'hover:bg-white/[0.05]'
                }`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => handleSelect(u)}
              >
                <p className="font-medium text-white">{u.display_name}</p>
                <p className="text-sm text-gray-400">
                  @{u.username}
                  <span className="text-gray-600"> · {ROLE_LABEL[u.role] || u.role}</span>
                </p>
              </button>
            ))}
        </div>
      )}
      {query.trim().length > 0 && query.trim().length < 2 && open && (
        <p className="mt-1 text-xs text-gray-500">Type at least 2 characters</p>
      )}
    </div>
  );
};

export default JudgeUserCombobox;
